import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { NO_WRAP, validate, mergeStyles } from 'wix-rich-content-common';
import pluginDividerSchema from 'wix-rich-content-common/dist/statics/schemas/plugin-divider.schema.json';
import { isEqual } from 'lodash';

const LINE_SINGLE = "single";
const LINE_DOUBLE = "double";
const ALIGN_LEFT = "left";
const ALIGN_CENTER = "center";
const ALIGN_RIGHT = "right";
const SIZE_SMALL = "small";
const SIZE_MEDIUM = "medium";
const SIZE_LARGE = "large";
const SIZES = Object.freeze([SIZE_LARGE, SIZE_MEDIUM, SIZE_SMALL]);
const ALIGNMENTS = [ALIGN_LEFT, ALIGN_CENTER, ALIGN_RIGHT];
const DEFAULTS = Object.freeze({
  type: LINE_SINGLE,
  config: {
    size: SIZE_LARGE,
    alignment: ALIGN_CENTER,
    textWrap: NO_WRAP
  }
});

var __defProp$3 = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$3(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$3(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const getNextValue = (array, currentValue) => array[(array.indexOf(currentValue) + 1) % array.length];
class Divider {
  constructor({ type, config } = {}) {
    this.getNextAlignmentConfig = () => {
      var _a;
      const alignment = getNextValue(ALIGNMENTS, (_a = this.config) == null ? void 0 : _a.alignment);
      return __spreadProps(__spreadValues$2({}, this.config), { alignment });
    };
    this.getNextSizeConfig = () => {
      var _a;
      const size = getNextValue(SIZES, (_a = this.config) == null ? void 0 : _a.size);
      return __spreadProps(__spreadValues$2({}, this.config), { size });
    };
    this.isAlignmentDisabled = () => this.size === SIZE_LARGE;
    this.type = type || DEFAULTS.type;
    this.config = __spreadValues$2(__spreadValues$2({}, DEFAULTS.config), config);
    this.size = this.config.size;
    this.alignment = this.config.alignment;
  }
}

var styles = {"divider-container":"PXpiE","divider-container--double":"Vgo36","divider":"xQWOa","divider--single":"ePfyU","divider--double":"AENoF","divider--dashed":"hzpP0","divider--dotted":"v8yIF","divider--small":"Nbsf1","divider--small--mobile":"m9rq-","divider--medium":"BtLsc","divider--large--mobile":"xTxoO","divider--medium--mobile":"MDHFi","divider--large":"Zxz4I","divider--left":"_4VvTF","divider--center":"k9PCx","divider--right":"EF-Za","divider--fill-parent":"eTnvv","svgFixerContainer":"_1M869","emptyChar":"-FPHJ"};

const withSafariSvgCaretFixer = (Component) => /* @__PURE__ */ React.createElement("div", { className: styles.svgFixerContainer }, Component, /* @__PURE__ */ React.createElement("div", { className: styles.emptyChar }, "\xA0"));

var __defProp$2 = Object.defineProperty;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
const lineProps = (width, lineDistance = 1) => ({
  x2: width,
  y1: lineDistance,
  y2: lineDistance
});
const getLines = (type, width, multilineDistance = 7) => {
  const linePropsArr = [lineProps(width)];
  if (type === LINE_DOUBLE) {
    linePropsArr.push(lineProps(width, multilineDistance));
  }
  return linePropsArr;
};
const DividerLine = ({
  type,
  size,
  alignment,
  width,
  multilineDistance,
  styles,
  className,
  fillParent,
  isMobile
}) => {
  const linesPropsArr = getLines(type, width, multilineDistance);
  const lineClassName = classNames(
    styles.divider,
    styles[`divider--${type}`],
    styles[`divider--${size}${isMobile ? "--mobile" : ""}`],
    styles[`divider--${alignment}`],
    fillParent ? styles["divider--fill-parent"] : "",
    className
  );
  return withSafariSvgCaretFixer(
    /* @__PURE__ */ React.createElement("svg", { className: lineClassName }, linesPropsArr.map((lineProp, i) => /* @__PURE__ */ React.createElement("line", __spreadValues$1({ key: i }, lineProp))))
  );
};
DividerLine.propTypes = {
  type: PropTypes.string.isRequired,
  size: PropTypes.string,
  alignment: PropTypes.string,
  styles: PropTypes.object.isRequired,
  className: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  multilineDistance: PropTypes.number,
  fillParent: PropTypes.bool,
  isMobile: PropTypes.bool
};
var DividerLine$1 = DividerLine;

const sizeClassName = (_componentData, _theme) => {
  return "";
};

var __defProp$1 = Object.defineProperty;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp$1(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class DividerComponent extends PureComponent {
  constructor(props) {
    super(props);
    validate(props.componentData, pluginDividerSchema);
  }
  componentWillReceiveProps(nextProps) {
    if (!isEqual(nextProps.componentData, this.props.componentData)) {
      validate(nextProps.componentData, pluginDividerSchema);
    }
  }
  render() {
    this.styles = this.styles || mergeStyles({ styles, theme: this.props.theme });
    const divider = new Divider(this.props.componentData);
    const { type, size, alignment } = divider;
    const className = classNames(
      this.styles["divider-container"],
      this.styles[`divider-container--${type}`],
      this.props.isMobile && this.styles["divider-container--mobile"],
      this.props.className
    );
    return /* @__PURE__ */ React.createElement("div", { className, "data-hook": `divider-${type}` }, /* @__PURE__ */ React.createElement(
      DividerLine$1,
      {
        type,
        width: "100%",
        size,
        alignment,
        styles: this.styles,
        isMobile: this.props.isMobile
      }
    ));
  }
}
__publicField(DividerComponent, "sizeClassName", (componentData, theme) => sizeClassName());
DividerComponent.propTypes = {
  componentData: PropTypes.object.isRequired,
  className: PropTypes.string,
  theme: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired
};
var DividerComponent$1 = DividerComponent;

const DIVIDER_TYPE = "wix-draft-plugin-divider";

const typeMapper = () => ({
  [DIVIDER_TYPE]: { component: DividerComponent$1, classNameStrategies: { size: sizeClassName } }
});

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
const pluginDivider = (config) => {
  return {
    config: __spreadValues(__spreadValues({}, DEFAULTS.config), config),
    type: DIVIDER_TYPE,
    typeMapper
    // nodeViewRenderers,
  };
};

export { DIVIDER_TYPE, typeMapper as dividerTypeMapper, pluginDivider };
//# sourceMappingURL=viewer.js.map

import 'wix-rich-content-plugin-divider/dist/es/styles.viewer.min.global.css'